/**
 * Export hook
 * Handles exporting places data to various formats
 */

import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@/stores/useNotificationStore';
import * as placesApi from '@/services/placesApi';
import {
  exportToCSV,
  exportToExcel,
  exportToJSON,
  generateExportFilename,
  isExportTooLarge,
} from '@/utils/exportUtils';
import type { ExportFormat, PlaceFilter, PlaceListItem, Place } from '@/types/place';
import type { ExportStatusResponse } from '@/types/api';

/**
 * Export state
 */
interface ExportState {
  isExporting: boolean;
  format: ExportFormat | null;
  progress: number;
  error: string | null;
}

/**
 * Query keys for exports
 */
const exportKeys = {
  status: (id: string) => ['export', 'status', id] as const,
};

/**
 * Hook for client-side export (for smaller datasets)
 */
export function useClientExport() {
  const [state, setState] = useState<ExportState>({
    isExporting: false,
    format: null,
    progress: 0,
    error: null,
  });
  const { success, error: showError } = useNotification();

  const exportData = useCallback(
    async (
      places: (PlaceListItem | Place)[],
      format: ExportFormat,
      options?: {
        filename?: string;
        jobQuery?: string;
        locationName?: string;
      }
    ) => {
      setState({
        isExporting: true,
        format,
        progress: 0,
        error: null,
      });

      try {
        const filename =
          options?.filename ||
          generateExportFilename(options?.jobQuery || 'export', options?.locationName);

        setState((prev) => ({ ...prev, progress: 50 }));

        switch (format) {
          case 'csv':
            exportToCSV(places, filename);
            break;
          case 'xlsx':
            exportToExcel(places, filename);
            break;
          case 'json':
            exportToJSON(places, filename);
            break;
        }

        setState((prev) => ({ ...prev, progress: 100 }));
        success('Export complete', `Downloaded ${places.length} places as ${format.toUpperCase()}`);

        // Reset state after a short delay
        setTimeout(() => {
          setState({
            isExporting: false,
            format: null,
            progress: 0,
            error: null,
          });
        }, 1000);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Export failed';
        setState({
          isExporting: false,
          format: null,
          progress: 0,
          error: errorMessage,
        });
        showError('Export failed', errorMessage);
      }
    },
    [success, showError]
  );

  return {
    ...state,
    exportToCSV: (places: (PlaceListItem | Place)[], options?: { filename?: string }) =>
      exportData(places, 'csv', options),
    exportToExcel: (places: (PlaceListItem | Place)[], options?: { filename?: string }) =>
      exportData(places, 'xlsx', options),
    exportToJSON: (places: (PlaceListItem | Place)[], options?: { filename?: string }) =>
      exportData(places, 'json', options),
    reset: () =>
      setState({
        isExporting: false,
        format: null,
        progress: 0,
        error: null,
      }),
  };
}

/**
 * Hook for server-side export (for larger datasets)
 */
export function useServerExport() {
  const queryClient = useQueryClient();
  const { success, error: showError, loading } = useNotification();
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);

  // Mutation to start export
  const startExport = useMutation({
    mutationFn: ({
      jobId,
      format,
      filter,
    }: {
      jobId: string;
      format: ExportFormat;
      filter?: PlaceFilter;
    }) => placesApi.exportPlaces(jobId, format, { filter }),
    onSuccess: (data) => {
      setCurrentExportId(data.id);
      loading('Preparing export...');
    },
    onError: (err) => {
      showError('Export failed', err instanceof Error ? err.message : 'Unknown error');
    },
  });

  // Query for export status (polling)
  const { data: exportStatus } = useQuery({
    queryKey: exportKeys.status(currentExportId || ''),
    queryFn: () => placesApi.getExportStatus(currentExportId!),
    enabled: !!currentExportId,
    refetchInterval: (query) => {
      const status = query.state.data as ExportStatusResponse | undefined;
      if (status?.status === 'completed' || status?.status === 'failed') {
        return false;
      }
      return 2000; // Poll every 2 seconds
    },
  });

  // Handle export completion
  const handleExportComplete = useCallback(
    (status: ExportStatusResponse) => {
      if (status.status === 'completed' && status.downloadUrl) {
        success('Export ready', 'Your export is ready for download');

        // Trigger download
        const link = document.createElement('a');
        link.href = status.downloadUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setCurrentExportId(null);
      } else if (status.status === 'failed') {
        showError('Export failed', status.error || 'Unknown error');
        setCurrentExportId(null);
      }
    },
    [success, showError]
  );

  // Watch for export completion
  if (exportStatus?.status === 'completed' || exportStatus?.status === 'failed') {
    if (currentExportId) {
      handleExportComplete(exportStatus);
    }
  }

  return {
    startExport: startExport.mutate,
    isStarting: startExport.isPending,
    exportStatus,
    isExporting: !!currentExportId && exportStatus?.status !== 'completed',
    progress: exportStatus?.progress ?? 0,
    cancel: () => setCurrentExportId(null),
  };
}

/**
 * Combined export hook that chooses client or server export based on size
 */
export function useExport(jobId?: string) {
  const clientExport = useClientExport();
  const serverExport = useServerExport();
  const { info } = useNotification();

  const exportPlaces = useCallback(
    async (
      places: (PlaceListItem | Place)[],
      format: ExportFormat,
      options?: {
        filename?: string;
        jobQuery?: string;
        locationName?: string;
        forceServerExport?: boolean;
      }
    ) => {
      // Determine whether to use client or server export
      if (options?.forceServerExport || isExportTooLarge(places.length)) {
        if (!jobId) {
          throw new Error('Job ID required for server export');
        }

        info(
          'Large export',
          `Exporting ${places.length.toLocaleString()} places. This may take a moment.`
        );

        serverExport.startExport({ jobId, format });
      } else {
        // Use client-side export for smaller datasets
        switch (format) {
          case 'csv':
            clientExport.exportToCSV(places, options);
            break;
          case 'xlsx':
            clientExport.exportToExcel(places, options);
            break;
          case 'json':
            clientExport.exportToJSON(places, options);
            break;
        }
      }
    },
    [jobId, clientExport, serverExport, info]
  );

  return {
    exportPlaces,
    isExporting: clientExport.isExporting || serverExport.isExporting,
    progress: clientExport.progress || serverExport.progress,
    error: clientExport.error,
    exportToCSV: clientExport.exportToCSV,
    exportToExcel: clientExport.exportToExcel,
    exportToJSON: clientExport.exportToJSON,
  };
}

export default useExport;
